import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import notificationPreferences from '../services/notificationPreferences';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import PlatformPageHeader from '../components/Common/PlatformPageHeader';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  useTheme as useMuiTheme
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import PaletteIcon from '@mui/icons-material/Palette';
import SettingsIcon from '@mui/icons-material/Settings';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LanguageIcon from '@mui/icons-material/Language';
import EmailIcon from '@mui/icons-material/Email';
import WebIcon from '@mui/icons-material/Web';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';

const Settings = () => {
  const { t } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const muiTheme = useMuiTheme();
  
  // Legacy local notifications state (kept for non-preference settings)
  const [notifications, setNotifications] = useState({
    marketing: false
  });

  // Real notification preferences from service
  const [notificationPrefs, setNotificationPrefs] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(true);

  const [preferences, setPreferences] = useState({
    currency: 'USD',
    timezone: 'UTC'
  });

  // Load notification preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await notificationPreferences.getPreferences();
        setNotificationPrefs(prefs);
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      } finally {
        setPrefsLoading(false);
      }
    };
    loadPreferences();
  }, []);

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Handle notification preference changes
  const handleNotificationPrefChange = async (category, type, value) => {
    try {
      const updatedPrefs = { ...notificationPrefs };
      
      if (type) {
        // Updating specific type within category
        if (!updatedPrefs[category]) updatedPrefs[category] = { enabled: true, types: {} };
        if (!updatedPrefs[category].types) updatedPrefs[category].types = {};
        updatedPrefs[category].types[type] = value;
      } else {
        // Updating category enabled status
        if (!updatedPrefs[category]) updatedPrefs[category] = { enabled: value, types: {} };
        else updatedPrefs[category].enabled = value;
      }

      const savedPrefs = await notificationPreferences.updatePreferences(updatedPrefs);
      setNotificationPrefs(savedPrefs);
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
    }
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const SectionHeader = ({ icon: Icon, title }) => (
    <Stack direction="row" alignItems="center" spacing={1} mb={3}>
      <Icon color="primary" />
      <Typography variant="h5" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
  );

  return (
    <SciFiBackground>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Navbar />
        <Box component="main" sx={{ flex: 1, py: { xs: 5, md: 8 }, px: 2 }}>
          <Container maxWidth="lg">
          <PlatformPageHeader
            icon={SettingsIcon}
            eyebrow="VITRIA / PREFERENCES"
            title={t('settings')}
            subtitle="Customize your account preferences and notifications"
          />

          <Grid container spacing={4}>
            {/* Theme & Language Settings */}
            <Grid item xs={12} lg={6}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  background: muiTheme.vitria?.input,
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  border: `1px solid ${muiTheme.palette.divider}`,
                }}
              >
                <SectionHeader icon={PaletteIcon} title="Appearance & Language" />
                
                <Stack spacing={4}>
                  {/* Theme Setting */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('theme')}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={toggleTheme}
                      startIcon={isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                      sx={{ 
                        borderColor: muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                        color: 'text.primary',
                        justifyContent: 'flex-start',
                        px: 3,
                        py: 1.5,
                        width: '100%'
                      }}
                    >
                      {isDarkMode ? t('lightMode') : t('darkMode')}
                    </Button>
                  </Box>

                  {/* Language Setting */}
                  <FormControl fullWidth>
                    <InputLabel>{t('language')}</InputLabel>
                    <Select
                      value={currentLanguage}
                      label={t('language')}
                      onChange={(e) => changeLanguage(e.target.value)}
                      startAdornment={<LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                    >
                      {languages.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.nativeName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Currency Setting */}
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={preferences.currency}
                      label="Currency"
                      onChange={(e) => handlePreferenceChange('currency', e.target.value)}
                    >
                      <MenuItem value="USD">USD - US Dollar</MenuItem>
                      <MenuItem value="EUR">EUR - Euro</MenuItem>
                      <MenuItem value="TND">TND - Tunisian Dinar</MenuItem>
                      <MenuItem value="MAD">MAD - Moroccan Dirham</MenuItem>
                      <MenuItem value="EGP">EGP - Egyptian Pound</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Timezone Setting */}
                  <FormControl fullWidth>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={preferences.timezone}
                      label="Timezone"
                      onChange={(e) => handlePreferenceChange('timezone', e.target.value)}
                    >
                      <MenuItem value="UTC">UTC - Coordinated Universal Time</MenuItem>
                      <MenuItem value="Africa/Tunis">Africa/Tunis - Tunisia</MenuItem>
                      <MenuItem value="Europe/Paris">Europe/Paris - France</MenuItem>
                      <MenuItem value="America/New_York">America/New_York - Eastern Time</MenuItem>
                      <MenuItem value="Asia/Dubai">Asia/Dubai - UAE</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Paper>
            </Grid>

            {/* Notifications Settings */}
            <Grid item xs={12} lg={6}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  background: muiTheme.vitria?.input,
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  border: `1px solid ${muiTheme.palette.divider}`,
                }}
              >
                <SectionHeader icon={NotificationsIcon} title="Notifications" />
                
                {prefsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Stack spacing={4}>
                    {/* Notification Methods */}
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', opacity: 0.9 }}>
                        Notification Methods
                      </Typography>
                      <Stack spacing={2}>
                        {[
                          { key: 'email', label: 'Email Notifications', desc: 'Receive updates via email', icon: EmailIcon },
                          { key: 'browser', label: 'Browser Notifications', desc: 'Desktop push notifications', icon: WebIcon },
                          { key: 'inApp', label: 'In-App Notifications', desc: 'Notifications within the app', icon: PhoneAndroidIcon }
                        ].map(item => (
                          <Paper 
                            key={item.key} 
                            variant="outlined" 
                            sx={{ p: 2, bgcolor: muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center">
                              <item.icon color="action" />
                              <Box>
                                <Typography variant="subtitle2">{item.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                              </Box>
                            </Stack>
                            <Switch
                              checked={notificationPrefs?.[item.key]?.enabled || false}
                              onChange={(e) => handleNotificationPrefChange(item.key, null, e.target.checked)}
                            />
                          </Paper>
                        ))}
                      </Stack>
                    </Box>

                    <Divider />

                    {/* Notification Types */}
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', opacity: 0.9 }}>
                        Notification Types
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                        Control which types of notifications you receive across all enabled methods
                      </Typography>
                      
                      <Stack spacing={3}>
                        {[
                          { key: 'bid_placed', label: 'Bid Placed', desc: 'When someone places a bid on your auctions' },
                          { key: 'outbid', label: 'Outbid Alerts', desc: 'When you\'ve been outbid on an item' },
                          { key: 'auction_ended', label: 'Auction Ended', desc: 'When auctions you\'re involved in end' },
                          { key: 'new_offer', label: 'New Offers', desc: 'When you receive offers on your items' },
                          { key: 'message', label: 'Messages', desc: 'New chat messages and communications' },
                          { key: 'general', label: 'General', desc: 'System updates and other notifications' }
                        ].map(item => {
                          const isEnabled = ['email', 'browser', 'inApp'].some(method => 
                            notificationPrefs?.[method]?.enabled && 
                            notificationPrefs?.[method]?.types?.[item.key] !== false
                          );

                          return (
                            <Box key={item.key}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Box>
                                  <Typography variant="subtitle2">{item.label}</Typography>
                                  <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                                </Box>
                                <Chip 
                                  label={isEnabled ? 'Enabled' : 'Disabled'} 
                                  color={isEnabled ? 'success' : 'default'} 
                                  size="small" 
                                  variant="outlined"
                                />
                              </Stack>
                              
                              <Grid container spacing={1}>
                                {['email', 'browser', 'inApp'].map(method => {
                                  const methodEnabled = notificationPrefs?.[method]?.enabled;
                                  const typeEnabled = notificationPrefs?.[method]?.types?.[item.key] !== false;
                                  
                                  return (
                                    <Grid item xs={4} key={method}>
                                      <Paper 
                                        variant="outlined" 
                                        sx={{ 
                                          p: 1, 
                                          bgcolor: muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', 
                                          opacity: methodEnabled ? 1 : 0.5,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between'
                                        }}
                                      >
                                        <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                                          {method === 'inApp' ? 'In-App' : method}
                                        </Typography>
                                        <Switch
                                          size="small"
                                          checked={typeEnabled}
                                          disabled={!methodEnabled}
                                          onChange={(e) => handleNotificationPrefChange(method, item.key, e.target.checked)}
                                        />
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>

                    <Divider />

                    {/* Legacy marketing preference */}
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', opacity: 0.9 }}>
                        Marketing
                      </Typography>
                      <Paper 
                        variant="outlined" 
                        sx={{ p: 2, bgcolor: muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      >
                        <Box>
                          <Typography variant="subtitle2">Promotional Emails</Typography>
                          <Typography variant="caption" color="text.secondary">Receive promotional emails and special offers</Typography>
                        </Box>
                        <Switch
                          checked={notifications.marketing}
                          onChange={() => handleNotificationChange('marketing')}
                        />
                      </Paper>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={async () => {
                          try {
                            const defaultPrefs = await notificationPreferences.resetToDefaults();
                            setNotificationPrefs(defaultPrefs);
                          } catch (error) {
                            console.error('Failed to reset preferences:', error);
                          }
                        }}
                      >
                        Reset to Defaults
                      </Button>
                    </Box>
                  </Stack>
                )}
              </Paper>
            </Grid>

            {/* Security Section */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  background: muiTheme.vitria?.input,
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  border: `1px solid ${muiTheme.palette.divider}`,
                }}
              >
                <SectionHeader icon={SecurityIcon} title="Security" />
                <Stack spacing={2}>
                  <Button variant="outlined" fullWidth sx={{ justifyContent: 'flex-start', py: 1.5, borderColor: muiTheme.palette.divider, color: 'text.primary' }}>
                    Change Password
                  </Button>
                  <Button variant="outlined" fullWidth sx={{ justifyContent: 'flex-start', py: 1.5, borderColor: muiTheme.palette.divider, color: 'text.primary' }}>
                    Two-Factor Authentication
                  </Button>
                  <Button variant="outlined" fullWidth sx={{ justifyContent: 'flex-start', py: 1.5, borderColor: muiTheme.palette.divider, color: 'text.primary' }}>
                    Login History
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            {/* Additional Settings */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  background: muiTheme.vitria?.input,
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  border: `1px solid ${muiTheme.palette.divider}`,
                }}
              >
                <SectionHeader icon={SettingsIcon} title="Additional Settings" />
                <Stack spacing={3}>
                  <Paper 
                    variant="outlined" 
                    sx={{ p: 2, bgcolor: muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box>
                      <Typography variant="subtitle2">Auto-save Preferences</Typography>
                      <Typography variant="caption" color="text.secondary">Automatically save settings changes</Typography>
                    </Box>
                    <Switch defaultChecked color="primary" />
                  </Paper>
                  
                  <Paper 
                    variant="outlined" 
                    sx={{ p: 2, bgcolor: muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box>
                      <Typography variant="subtitle2">Beta Features</Typography>
                      <Typography variant="caption" color="text.secondary">Enable experimental features</Typography>
                    </Box>
                    <Switch color="default" />
                  </Paper>
                </Stack>
              </Paper>
            </Grid>

            {/* Save Button */}
            <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
              <Button 
                variant="contained" 
                size="large"
                sx={{ 
                  px: 6, 
                  py: 1.5,
                }}
              >
                {t('save')} All Settings
              </Button>
            </Grid>
          </Grid>
          </Container>
        </Box>
      </Box>
    </SciFiBackground>
  );
};

export default Settings;
